import classNames from 'classnames';
import { ReactNode, useEffect, useState } from 'react';

import { AccordionItem } from 'common/accordion/accordion-item.js';
import { ISeverity } from 'utilities/weather-warnings/warning-severity.js';

interface AccordionOldProps {
  headers: Array<ReactNode>;
  bodies: Array<ReactNode>;
  className?: string;
  bodyClassName?: string;
  openFirstChildAsDefault?: boolean;
  restrictToOnlyOneChildOpen?: boolean;
  warningsSeverities?: Array<ISeverity>;
  defaultToOpen?: boolean;
  defaultOpenLength?: number;
  dataComponent: string;
}

/**
 * Takes in a list of headers and bodies to render of the accordion tabs
 *
 * |*************|***|
 * | Header      | V |
 * |*************|***|
 * |                 |
 * |      Body       |
 * |                 |
 * |*****************|
 *
 * The headers prop matches directly to the bodies prop (i.e. their lengths should be the same).
 * By default there are no tabs are open and any number can be open at once.
 * @deprecated Use AccordionNew when possible
 */
export const Accordion = ({
  headers,
  bodies,
  className,
  bodyClassName,
  warningsSeverities,
  defaultToOpen,
  restrictToOnlyOneChildOpen,
  openFirstChildAsDefault,
  defaultOpenLength = 2,
  dataComponent,
}: AccordionOldProps) => {
  const getToggles = (headers: Array<ReactNode>, openFirstChildAsDefault: boolean) => {
    const defaultToggle = headers.length <= defaultOpenLength;
    const toggles = headers.map(() => defaultToggle || !!defaultToOpen);

    if (openFirstChildAsDefault) {
      toggles[0] = true;
    }

    return toggles;
  };

  const [accordionState, setAccordionState] = useState(getToggles(headers, openFirstChildAsDefault || false));

  useEffect(() => {
    setAccordionState(getToggles(headers, openFirstChildAsDefault || false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers, openFirstChildAsDefault]);

  const toggleCallback = (index: number) => {
    setAccordionState((prev) => {
      const newAccordionState = [...prev];
      const oldStateOfIndex = prev[index];

      if (restrictToOnlyOneChildOpen) {
        newAccordionState.fill(false, 0, headers.length);
      }

      newAccordionState[index] = !oldStateOfIndex;

      return newAccordionState;
    });
  };

  const accordionClasses = classNames('Accordion', className);

  return (
    <ul className={accordionClasses} data-component={'accordian '.concat(dataComponent)}>
      {headers.map((heading, index) => (
        <AccordionItem
          key={index}
          body={bodies[index]}
          heading={heading}
          toggleCallback={toggleCallback}
          index={index}
          bodyClassName={bodyClassName}
          isOpen={accordionState[index]}
          warningSeverityClass={warningsSeverities?.[index]?.colorClass}
        />
      ))}
    </ul>
  );
};
